/** @file Document invariants beyond the schema (the commit gate): names, references, structure, rules. */
import type { Doc } from './schema';
import { ENTITY_KINDS, type EntityKind } from './entityKinds';
import { names, type NameKind } from '../names';
import { makeError, type Pose2DError } from '../pose2dError';
import { refChecker, type NeedRef } from './refChecker';
import { isSlotPermutation } from './isSlotPermutation';
import { checkDocAnimations } from './checkDocAnimations';
import { checkDocDirections } from './checkDocDirections';
import { checkDocIk } from './checkDocIk';
import { checkDocIkCycles } from './checkDocIkCycles';
import { checkDocMounts } from './checkDocMounts';
import { checkDocMeshes } from './checkDocMeshes';
import { checkDocSockets } from './checkDocSockets';

const KIND_TO_NAME_KIND: Record<EntityKind, NameKind> = {
  image: 'image', bone: 'bone', slot: 'slot', attachment: 'attachment', skin: 'skin', ik: 'ik',
  event: 'event', socket: 'socket', animation: 'animation', reference: 'reference',
};

function checkNames(doc: Doc, out: Pose2DError[]): void {
  for (const kind of Object.keys(ENTITY_KINDS) as EntityKind[]) {
    const table = doc[ENTITY_KINDS[kind]] as Record<string, { name: string }>;
    const seen = new Map<string, string>();
    for (const [id, entity] of Object.entries(table)) {
      const path = `${ENTITY_KINDS[kind]}.${id}.name`;
      if (!names.isValid(KIND_TO_NAME_KIND[kind], entity.name)) out.push(makeError('INVALID_NAME', { name: entity.name, kind }, { path }));
      const dup = seen.get(entity.name);
      if (dup) out.push(makeError('NAME_TAKEN', { kind, name: entity.name }, { path, hint: `also used by ${dup}` }));
      seen.set(entity.name, id);
    }
  }
  if (!names.isValid('project', doc.meta.name)) out.push(makeError('INVALID_NAME', { name: doc.meta.name, kind: 'project' }, { path: 'meta.name' }));
}

function checkSkeletonRefs(doc: Doc, need: NeedRef): void {
  for (const [id, b] of Object.entries(doc.bones)) if (b.parent) need(b.parent, 'bone', `bones.${id}.parent`);
  for (const [id, s] of Object.entries(doc.slots)) need(s.bone, 'bone', `slots.${id}.bone`);
  for (const [id, c] of Object.entries(doc.constraints)) { need(c.target, 'bone', `constraints.${id}.target`); c.bones.forEach((b, i) => { need(b, 'bone', `constraints.${id}.bones.${i}`); }); }
  for (const [id, s] of Object.entries(doc.sockets)) {
    need(s.parentBone, 'bone', `sockets.${id}.parentBone`);
    need(s.bone, 'bone', `sockets.${id}.bone`);
    if (s.grip2Bone !== undefined) need(s.grip2Bone, 'bone', `sockets.${id}.grip2Bone`);
  }
}

function checkAttachmentRefs(doc: Doc, need: NeedRef): void {
  for (const [id, a] of Object.entries(doc.attachments)) {
    need(a.image, 'image', `attachments.${id}.image`);
    if (a.mount) need(a.mount.socket, 'socket', `attachments.${id}.mount.socket`);
  }
  for (const [id, sk] of Object.entries(doc.skins)) sk.entries.forEach((e, i) => { need(e.slot, 'slot', `skins.${id}.entries.${i}.slot`); need(e.attachment, 'attachment', `skins.${id}.entries.${i}.attachment`); });
  need(doc.defaultSkin, 'skin', 'defaultSkin');
  for (const [id, r] of Object.entries(doc.references)) need(r.image, 'image', `references.${id}.image`);
}

function checkDrawOrder(doc: Doc, out: Pose2DError[]): void {
  if (!isSlotPermutation(doc, doc.drawOrder)) out.push(makeError('VALIDATION', {}, { path: 'drawOrder', hint: 'drawOrder must list every slot exactly once' }));
}

function checkBoneCycles(doc: Doc, out: Pose2DError[]): void {
  for (const start of Object.keys(doc.bones)) {
    const seen = new Set<string>();
    let cur: string | null = start;
    while (cur) {
      if (seen.has(cur)) { out.push(makeError('CYCLE', { path: [...seen, cur].join(' -> ') }, { path: `bones.${start}.parent` })); break; }
      seen.add(cur);
      cur = doc.bones[cur]?.parent ?? null;
    }
  }
}

function checkRoot(doc: Doc, out: Pose2DError[]): void {
  const roots = Object.entries(doc.bones).filter(([, b]) => b.parent === null).map(([id]) => id);
  if (roots.length === 1) return;
  const found = roots.length === 0 ? 'none' : roots.join(', ');
  out.push(makeError('VALIDATION', {}, { path: 'bones', hint: `exactly one bone must have parent null (found ${roots.length}: ${found})` }));
}

function checkPlaceholders(doc: Doc, out: Pose2DError[]): void {
  for (const [id, skin] of Object.entries(doc.skins)) {
    const seen = new Set<string>();
    skin.entries.forEach((e, i) => {
      const key = `${e.slot}/${e.placeholder}`;
      if (seen.has(key)) out.push(makeError('NAME_TAKEN', { kind: 'placeholder', name: e.placeholder }, { path: `skins.${id}.entries.${i}.placeholder`, hint: `slot ${e.slot} already maps this placeholder in this skin` }));
      seen.add(key);
    });
  }
}

/**
 * Returns every invariant violation (empty when valid). MIRROR_ASYMMETRY is advisory and comes from
 * `mirrorAsymmetry` (a Warning), never from here, because validateDoc gates every commit.
 */
export function validateDoc(doc: Doc): Pose2DError[] {
  const out: Pose2DError[] = [];
  const need = refChecker(doc, out);
  checkNames(doc, out);
  checkSkeletonRefs(doc, need);
  checkAttachmentRefs(doc, need);
  checkDrawOrder(doc, out);
  checkBoneCycles(doc, out);
  checkRoot(doc, out);
  checkPlaceholders(doc, out);
  checkDocSockets(doc, out);
  checkDocAnimations(doc, need, out);
  checkDocDirections(doc, need, out);
  checkDocIk(doc, out);
  checkDocIkCycles(doc, out);
  checkDocMounts(doc, out);
  checkDocMeshes(doc, need, out);
  return out;
}
